import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toPng } from "html-to-image";
import { Download, Linkedin } from "lucide-react";
import { toast } from "sonner";
import latexBadgeBg from "@/assets/latex-badge-bg.png";

interface CertificateBadgeProps {
  userName: string;
  certificateNumber: string;
  issuedAt: string;
  canDownload: boolean;
}

export function CertificateBadge({ userName, certificateNumber, issuedAt, canDownload }: CertificateBadgeProps) {
  const { t } = useTranslation();
  const badgeRef = useRef<HTMLDivElement>(null);

  const downloadBadge = async () => {
    if (!badgeRef.current || !canDownload) return;

    try {
      const dataUrl = await toPng(badgeRef.current, {
        quality: 1,
        pixelRatio: 3,
        backgroundColor: 'transparent',
      });

      const link = document.createElement('a');
      link.download = `calclab-latex-badge-${certificateNumber}.png`;
      link.href = dataUrl;
      link.click();
      toast.success(t('latexCenter.badge.downloadSuccess', 'Badge downloaded!'));
    } catch (error) {
      console.error('Error generating badge:', error);
      toast.error(t('latexCenter.badge.downloadError', 'Failed to download badge'));
    }
  };

  const shareToLinkedIn = () => {
    const certUrl = `${window.location.origin}/verify/certificate?id=${certificateNumber}`;
    const linkedInUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent('LaTeX Professional Certification')}&organizationName=${encodeURIComponent('CalcLab')}&issueYear=${new Date(issuedAt).getFullYear()}&issueMonth=${new Date(issuedAt).getMonth() + 1}&certUrl=${encodeURIComponent(certUrl)}&certId=${encodeURIComponent(certificateNumber)}`;
    window.open(linkedInUrl, '_blank');
  };

  const formattedDate = new Date(issuedAt).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric'
  });

  return (
    <div className="space-y-4">
      {/* Badge Preview */}
      <div className="flex justify-center">
        <div
          ref={badgeRef}
          className="relative w-[300px] h-[420px] rounded-2xl overflow-hidden shadow-xl"
          style={{
            userSelect: canDownload ? 'auto' : 'none',
            pointerEvents: canDownload ? 'auto' : 'none',
          }}
        >
          {/* Background Image */}
          <img 
            src={latexBadgeBg} 
            alt="Badge background"
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
          
          {/* Overlay for better text visibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a4a5e]/30 via-transparent to-[#1a4a5e]/50" />
          
          {/* Content */}
          <div className="absolute inset-0 flex flex-col justify-between p-4">
            {/* Top - CalcLab Logo */}
            <div className="text-center">
              <h1 
                className="text-3xl font-bold tracking-wider text-white"
                style={{ 
                  fontFamily: 'MuseoModerno, sans-serif',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)' 
                }}
              >
                CALCLAB.
              </h1>
            </div>
            
            {/* Middle - Empty for background design */}
            <div className="flex-1" />
            
            {/* Bottom - LaTeX Certified */}
            <div className="text-center space-y-1">
              <h2 
                className="text-3xl font-bold tracking-wide text-white"
                style={{ 
                  fontFamily: 'MuseoModerno, serif',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)' 
                }}
              >
                LaT<span className="text-2xl relative -top-[2px]">E</span>X
              </h2>
              <p 
                className="text-xl font-medium tracking-widest text-gray-200"
                style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}
              >
                CERTIFIED
              </p>
              <div className="pt-2 text-xs text-gray-300/80 space-y-0.5">
                <p>{userName}</p>
                <p>{formattedDate}</p>
                <p className="font-mono text-[10px]">{certificateNumber}</p>
              </div>
            </div>
          </div>
          
          {/* Watermark for non-downloadable */}
          {!canDownload && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <p className="text-white/80 text-lg font-medium rotate-[-20deg]">
                {t('latexCenter.badge.completeToUnlock', 'Complete course to unlock')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {canDownload && (
        <div className="flex justify-center gap-3">
          <Button onClick={downloadBadge} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            {t('latexCenter.badge.downloadPNG', 'Download Badge')}
          </Button>
          <Button onClick={shareToLinkedIn} className="bg-[#0077B5] hover:bg-[#005885]">
            <Linkedin className="h-4 w-4 mr-2" />
            {t('latexCenter.badge.addToLinkedIn', 'Add to LinkedIn')}
          </Button>
        </div>
      )}
      
      {canDownload && (
        <p className="text-center text-xs text-muted-foreground">
          {t('latexCenter.badge.verifyText', 'This badge can be verified at')}{' '}
          <a 
            href={`/verify/certificate?id=${certificateNumber}`}
            className="text-primary hover:underline"
          >
            calclab.app/verify/certificate
          </a>
        </p>
      )}
    </div>
  );
}
